import React, { useState, useEffect } from 'react'
import { fieldSampleSetAPI } from '../services/api'
import { Plus, Edit, Trash2, Save, X, Search, Layers } from 'lucide-react'

/**
 * Field Sample Sets management.
 *
 * Stores reusable (field_name, field_value) pairs grouped by a `group` label.
 * All rows sharing a group make up one coherent sample set that can be applied
 * together when generating design samples.
 */
const FieldSampleSetsManagement = () => {
  const [samples, setSamples] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Create / edit form state
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({ field_name: '', field_value: '', group: '' })

  useEffect(() => {
    fetchSamples()
  }, [])

  const fetchSamples = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fieldSampleSetAPI.getAll({ limit: 1000 })
      setSamples(response.data.data || [])
    } catch (err) {
      console.error('Error fetching field sample sets:', err)
      setError('Failed to load field sample sets')
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({ field_name: '', field_value: '', group: '' })
    setEditingId(null)
    setShowForm(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.field_name.trim()) {
      setError('Field name is required')
      return
    }
    try {
      setSaving(true)
      setError(null)
      const payload = {
        field_name: formData.field_name.trim(),
        field_value: formData.field_value,
        group: formData.group.trim() || null,
      }
      if (editingId) {
        await fieldSampleSetAPI.update(editingId, payload)
        setSuccess('Sample updated')
      } else {
        await fieldSampleSetAPI.create(payload)
        setSuccess('Sample added')
      }
      resetForm()
      fetchSamples()
    } catch (err) {
      console.error('Error saving field sample set:', err)
      setError(err.response?.data?.message || 'Failed to save sample')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (sample) => {
    setFormData({
      field_name: sample.field_name || '',
      field_value: sample.field_value || '',
      group: sample.group || '',
    })
    setEditingId(sample.id)
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sample value?')) return
    try {
      await fieldSampleSetAPI.delete(id)
      setSuccess('Sample deleted')
      fetchSamples()
    } catch (err) {
      console.error('Error deleting field sample set:', err)
      setError('Failed to delete sample')
    }
  }

  // Pre-fill a new row for a specific group so a set can be built field by field.
  const handleAddToGroup = (group) => {
    setFormData({ field_name: '', field_value: '', group: group || '' })
    setEditingId(null)
    setShowForm(true)
  }

  // Filter then group the rows by their `group` label.
  const term = searchTerm.trim().toLowerCase()
  const filtered = samples.filter((s) => {
    if (!term) return true
    return (
      (s.group || '').toLowerCase().includes(term) ||
      (s.field_name || '').toLowerCase().includes(term) ||
      (s.field_value || '').toLowerCase().includes(term)
    )
  })

  const grouped = filtered.reduce((acc, s) => {
    const key = s.group || '(no group)'
    if (!acc[key]) acc[key] = []
    acc[key].push(s)
    return acc
  }, {})
  const groupNames = Object.keys(grouped).sort()

  return (
    <div>
      {/* Header + actions */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Field Samples</h3>
          <p className="text-sm text-gray-500">
            Store sets of sample field values. Each group is one complete set used when generating design samples.
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true) }}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Sample
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <p className="text-red-600 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <p className="text-green-600 text-sm">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Create / Edit form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
              <input
                type="text"
                value={formData.group}
                onChange={(e) => setFormData((p) => ({ ...p, group: e.target.value }))}
                placeholder="e.g. set-1"
                list="field-sample-groups"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <datalist id="field-sample-groups">
                {groupNames.filter((g) => g !== '(no group)').map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Name *</label>
              <input
                type="text"
                value={formData.field_name}
                onChange={(e) => setFormData((p) => ({ ...p, field_name: e.target.value }))}
                placeholder="e.g. candidate_name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Value</label>
              <input
                type="text"
                value={formData.field_value}
                onChange={(e) => setFormData((p) => ({ ...p, field_value: e.target.value }))}
                placeholder="e.g. Sachin"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            <button type="button" onClick={resetForm} className="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {/* Search */}
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search by group, field name, or value..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Grouped listing */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : groupNames.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Layers className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>No field samples yet. Add one to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groupNames.map((groupName) => (
            <div key={groupName} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-900">{groupName}</span>
                  <span className="text-xs text-gray-500">({grouped[groupName].length} fields)</span>
                </div>
                <button
                  onClick={() => handleAddToGroup(groupName === '(no group)' ? '' : groupName)}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus className="w-3 h-3" /> Add field
                </button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Field Name</th>
                    <th className="px-4 py-2 font-medium">Field Value</th>
                    <th className="px-4 py-2 font-medium w-24 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[groupName].map((sample) => (
                    <tr key={sample.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-gray-800">{sample.field_name}</td>
                      <td className="px-4 py-2 text-gray-700 break-words">{sample.field_value}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleEdit(sample)} className="text-gray-400 hover:text-blue-600" title="Edit">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(sample.id)} className="text-gray-400 hover:text-red-600" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default FieldSampleSetsManagement
